import React from "react"
import { Badge } from "reactstrap"
import { Link } from "react-router-dom"
import { getParticipantGameLevel } from "../../components/participantLevel"
import { luckyDrawSeeds } from "../../components/seeds"
import DrawPlayerDetails from "../../components/DrawPlayerDetails"
import { entryUser } from "components/ReviewAudit/PublicRatingScope"
import { mainListPlaces } from "../../../../../mainListPlaces"
import Participant, { participantNumber as numeric, participantStatistic as statistic, entryFee as fee } from "../../components/ParticipantSummary"
import "./entries.scss"

const lists = [
  ["mainList", "Main List", "entries-main-list"],
  ["waitingList", "Waiting List", "entries-waiting-list"],
  ["preRegistrationList", "Sorting List", "entries-sorting-list"],
]
const active = entry => !["WITHDRAWN", "DELETED"].includes(entry.status)
const confirmed = entries => entries.filter(entry => entry.status === "CONFIRMED")
const number = value => Number.isInteger(value) && value >= 0 ? value : null
const display = value => value === null ? "Unknown" : value

const TeamAverages = ({ user, partner }) => {
  const average = (left, right) => left !== null && right !== null ? Number(((left + right) / 2).toFixed(2)) : "N/A"
  return <div className="entries-team-averages text-muted">
    <span>Avg. level: {average(numeric(getParticipantGameLevel(user, "doubles")), numeric(getParticipantGameLevel(partner, "doubles")))}</span>
    <span>Avg. points: {average(statistic(user, "points", "doubles"), statistic(partner, "points", "doubles"))}</span>
    <span>Avg. rank: {average(statistic(user, "rank", "doubles"), statistic(partner, "rank", "doubles"))}</span>
  </div>
}

const PayerDetails = ({ entry }) => {
  const payerId = entry.userId ?? entry.user?.userId
  const payer = [entry.user, entry.partner].find(person => payerId != null && String(person?.userId) === String(payerId))
  const profileId = payer?.id || payer?._id
  const payerName = [payer?.firstName, payer?.lastName].filter(Boolean).join(" ")
  const description = payerId != null ? `${payerName || "Account"} (#${payerId})` : "Not recorded"
  const paymentMessage = numeric(entry.amount) === 0
    ? "No charge recorded for this entry."
    : entry.paid === true
    ? "Payment recorded for the whole team."
    : entry.paid === false
    ? "Assigned payer; this entry is not marked as paid."
    : "Assigned payer; payment status is unknown."
  return <>
    <dt>Payer account</dt>
    <dd>
      {profileId ? <Link to={`/accounts/players/${profileId}`} target="_blank" rel="noopener noreferrer">{description}</Link> : description}
      <div className="text-muted">{paymentMessage}</div>
    </dd>
  </>
}

// `wildCardAction(position)` renders the add control of a reserved WC place that no player holds yet.
export default function EntryLists({ competition, eventDetails, Actions, wildCardAction, toolbar, actionFeedback }) {
  const main = Array.isArray(competition.mainList) ? confirmed(competition.mainList) : null
  const capacity = number(competition.mlMaxNumber)
  const reserved = number(competition.wildCards)
  const occupiedWC = main?.filter(entry => entry.slotType === "WILD_CARD").length ?? null
  const standard = main ? main.length - occupiedWC : null
  const availableStandard = capacity !== null && reserved !== null && standard !== null ? Math.max(0, capacity - reserved - standard) : null
  const availableWC = reserved !== null && occupiedWC !== null ? Math.max(0, reserved - occupiedWC) : null
  // Each reserved WC place is a Main List place, whether or not a player holds it yet (mainListPlaces.js); an open one shows as its own row.
  const places = mainListPlaces({ entries: main?.length, occupied: occupiedWC, reserved })
  const seedOf = luckyDrawSeeds(competition)
  return <div className="competition-entries">
    <DrawPlayerDetails entries={lists.flatMap(([key]) => competition[key] || []).filter(active)}>
    <div className="entries-toolbar">
      <div className="text-muted">Standard places available: {display(availableStandard)} · Reserved WC available: {display(availableWC)}</div>
      {toolbar}
    </div>
    <div className="entries-lists">
      {lists.map(([key, title, id]) => {
        const entries = competition[key]
        if (key === "preRegistrationList" && Array.isArray(entries) && !entries.length) return null
        const rows = Array.isArray(entries) ? entries.filter(active) : null
        return <section key={key} id={id} aria-labelledby={`${id}-title`} className={`entries-list entries-list--${key}`} tabIndex={-1}>
          <header className="entries-list-heading"><h3 id={`${id}-title`}>{title}</h3>
            {key === "mainList"
              ? <span className="entries-occupancy" title="Main List places: confirmed entries and reserved WildCard places">{display(places.taken)} / {display(capacity)} ML · incl. {display(places.occupied)}/{display(places.reserved)} WC</span>
              : <span>{rows ? `${confirmed(rows).length} confirmed` : "Unavailable"}</span>}
          </header>
          {!rows && <p role="status">Entries could not be loaded. Refresh the competition to retry.</p>}
          {rows?.length === 0 && !(key === "mainList" && places.open > 0) && <p className="text-muted">No entries</p>}
          {rows?.map((entry, index) => <article className="entries-row" key={entry._id}>
            <span className="entries-position" aria-label={`Position ${index + 1}`}>{index + 1}</span>
            <div className={`entries-team${competition.gameType === "doubles" ? " entries-team--doubles" : ""}`}>
              <div className="entries-participants">
                <Participant copyContacts person={entryUser(entry)} fallbackId={entry.userId} seedRank={seedOf(entry)} gameType={competition.gameType} currency={entry.currency} eventDetails={eventDetails} />
                {competition.gameType === "doubles" && <Participant copyContacts person={entry.partner} fallbackId={entry.partnerId} gameType={competition.gameType} currency={entry.currency} eventDetails={eventDetails} />}
              </div>
              <div className="entries-team-summary">
                {competition.gameType === "doubles" && <TeamAverages user={entry.user} partner={entry.partner} />}
                <div className="entries-badges">
                  {entry.slotType === "WILD_CARD" && <Badge color="info">WC</Badge>}
                  {entry.status !== "CONFIRMED" && <Badge color="warning">{entry.status || "Status unknown"}</Badge>}
                  <Badge color={entry.paid === true ? "success" : entry.paid === false ? "danger" : "light"}>{entry.paid === true ? "Paid" : entry.paid === false ? "Unpaid" : "Payment unknown"}</Badge>
                  <span>{competition.gameType === "doubles" ? "Team fee" : "Entry fee"}: {fee(entry.amount, entry.currency)}</span>
                </div>
                {entry.createdBy && <div className="entries-created-by text-muted">Created by: {entry.createdBy}{entry.createdById != null ? ` #${entry.createdById}` : ""}</div>}
              </div>
              <div className="entries-entry-footer">
                <details><summary>Entry details</summary>
                  <dl className="entries-facts">
                    {competition.gameType === "doubles" && <PayerDetails entry={entry} />}
                    <dt>Entry ID</dt><dd>{entry._id}</dd>
                    <dt>Registered</dt><dd>{entry.registeredAt || entry.createdAt || "Unknown"}</dd>
                    {entry.waitingListEnteredAt && <><dt>Joined Waiting List</dt><dd>{entry.waitingListEnteredAt}</dd></>}
                  </dl>
                </details>
                {entry.status === "CONFIRMED" && Actions && <div className="entries-actions"><Actions _id={entry._id} noMove={key === "preRegistrationList"} list={key === "mainList" ? "waitingList" : key === "waitingList" ? "mainList" : "preEntries"} /></div>}
              </div>
              {actionFeedback?.entryId === entry._id && <div className="entries-action-feedback">{actionFeedback.content}</div>}
            </div>
          </article>)}
          {key === "mainList" && rows && Array.from({ length: places.open || 0 }, (_, index) => <article className="entries-row entries-row--wc-place" key={`wc-place-${index}`}>
            <span className="entries-position" aria-label={`Position ${rows.length + index + 1}`}>{rows.length + index + 1}</span>
            <div className="entries-wc-place"><Badge color="info">WC</Badge><span>Reserved WC place · not assigned</span>
              {wildCardAction?.(rows.length + index + 1)}</div>
          </article>)}
        </section>
      })}
    </div>
    </DrawPlayerDetails>
  </div>
}
